import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Loader from 'components/partials/loader/loader';
import NotificationMessage from 'components/partials/notification/notification';
import Heading from 'components/partials/heading/heading';
import Button from 'components/partials/button/button';
import PowerBiReport from 'components/shared/others/power-bi-report/power-bi-report';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';

import { getReportById } from 'api/generated/report-controller/report-controller';

import { useClient } from 'hooks/api/use-clients';
import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { openScreenshotCapture } from 'helpers/utils/clipboard-screenshot';

import { REPORT_TYPES } from 'constants/reports';

interface ReportData {
  id: string | number;
  title?: string;
  powerBiId?: string;
  productType?: string;
  [key: string]: unknown;
}

interface ReportMetadata {
  activePage?: string | null;
  filters?: unknown[];
}

interface FetchError {
  status?: number;
  statusText?: string;
}

interface ClientReportViewProps {
  clientId: string | number | undefined;
  id: string | number;
}

const ClientReportView = ({
  clientId,
  id: reportId,
}: ClientReportViewProps): React.JSX.Element | null => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<FetchError | null>(null);
  const [reportMetadata, setReportMetadata] = useState<ReportMetadata>({
    activePage: null,
    filters: [],
  });

  const location = useLocation();
  const currentUser = useCurrentUser();
  const currentClient = useClient(clientId);
  const userId = (currentUser?.data as { oktaUserId?: string } | null | undefined)
    ?.oktaUserId;
  const reportRef = useRef<HTMLDivElement>(null);

  const powerbiRelatedReportPageId =
    (location.state as { powerbiRelatedReportPageId?: string } | null)
      ?.powerbiRelatedReportPageId || null;

  useEffect(() => {
    const fetchReport = async (): Promise<void> => {
      try {
        const data = await getReportById(userId ?? '', Number(reportId));

        setReportData((data as ReportData) ?? null);
      } catch (e) {
        setError(e as FetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId, userId]);

  const handleReportError = (err: unknown): void => setError(err as FetchError);
  const handleMetadata = (meta?: ReportMetadata): void =>
    setReportMetadata(meta || {});

  if (loading) {
    return <Loader loading={true} />;
  }

  if (error) {
    return (
      <ResponsiveWrapper>
        <NotificationMessage
          customClass="mt-6"
          heading="Error loading report"
          variant="error"
          message={`There was an error loading this report. (${error?.status} ${error?.statusText})`}
        />
      </ResponsiveWrapper>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <ResponsiveWrapper>
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Clients', href: '/admin/clients' },
          {
            label: currentClient?.data?.name ?? '',
            href: `/admin/clients/${clientId}`,
          },
          { label: 'Reports', href: `/admin/clients/${clientId}/reports` },
          { label: reportData.title ?? '', isActive: true },
        ]}
      />
      <div className="flex justify-between items-center">
        <div>
          <p className="mt-4 text-xs uppercase text-gray-600 tracking-widest font-text">
            Currently Viewing
          </p>
          <Heading customClass="mr-2" variant="h1">
            {reportData.title}
          </Heading>
        </div>

        <Button
          variant="reserve-outlined"
          size="sm"
          onClick={() =>
            openScreenshotCapture(reportRef.current, {
              isReport: true,
              name: reportData?.title,
              title: '',
              description: '',
              reportActivePage: reportMetadata?.activePage || null,
              reportFilters: reportMetadata?.filters || [],
              reportId: reportData?.id,
              clientId,
              relatedGoals: [],
              source: 'report',
            })
          }
        >
          Add Insight
        </Button>
      </div>
      <div className="mt-6 min-h-[300px] relative" ref={reportRef}>
        <PowerBiReport
          reportId={reportData.powerBiId ?? ''}
          relatedPageId={powerbiRelatedReportPageId ?? undefined}
          fitToWidth={
            reportData.productType === REPORT_TYPES.FINANCIAL_AID_VIEW
          }
          onError={handleReportError}
          onMetadata={handleMetadata as never}
          clientId={clientId as never}
        />
      </div>
    </ResponsiveWrapper>
  );
};

export default ClientReportView;
